import { useEffect, useRef } from "react";
import {
  compact,
  diffMoves,
  type GridLayoutItem,
  type WidgetInstance,
  type WidgetMove,
} from "@salestrak/ui";

import { managedSeed, type ManagedWidget } from "#app/widgets/dashboards/managed-widgets";
import type { WidgetViewStore } from "#app/widgets/dashboards/use-widget-view";
import type { Omittable } from "@salestrak/ui";

/**
 * The managed surface's CANONICAL-board maintenance: three ordered effects
 * that keep the shared default board healthy. (First-visit seeding is NOT
 * one of them — the SERVER lazily seeds a view's default board on its first
 * read, `apps/api/core/src/widgets/widget-view-defaults.ts`.) They gate on
 * `canMaintain` — `access.canWrite` while the store is NOT in personal mode
 * (never a personalizing viewer, and never an editor whose lens is on their
 * personal fork: maintenance corrects the canonical board, not a fork) AND
 * while the staged working copy is NOT dirty (the corrections write through
 * to the server, but the first staged edit froze the baseline — a mid-edit
 * correction would leave the frozen baseline holding server-deleted rows,
 * and Discard would resurrect them; deferring until save/discard keeps
 * baseline and server in lockstep, and the effects dep on the gate so they
 * re-run once dirty clears) — and write through `store.direct.*`, so they
 * bypass staging and never flip `dirty`.
 *
 * Order matters: duplicate repair retires extra rows one per pass; the
 * `managedLayoutVersion` migration waits while duplicates exist so it never
 * migrates a snapshot mid-repair; the gravity self-heal waits for BOTH (and
 * for in-flight optimistic adds) before it compacts away any vertical slack.
 */

/** The `widgetKey` of every placed instance that carries one. */
function placedWidgetKeys(widgets: WidgetInstance[]): string[] {
  return widgets.flatMap((widget) => {
    const key = (widget.config as { widgetKey?: unknown } | null)?.widgetKey;
    return typeof key === "string" ? [key] : [];
  });
}

/** True while two placed instances share a `widgetKey` — repair in progress. */
function hasDuplicateKeys(widgets: WidgetInstance[]): boolean {
  const keys = placedWidgetKeys(widgets);
  return new Set(keys).size !== keys.length;
}

/** The catalog's highest baked `managedLayoutVersion` (0 = unversioned). */
function migrationTargetVersion(catalog: ManagedWidget[]): number {
  return catalog.reduce((version, widget) => {
    const candidate = (widget.config as { managedLayoutVersion?: unknown }).managedLayoutVersion;
    return typeof candidate === "number" ? Math.max(version, candidate) : version;
  }, 0);
}

/** True while any placed instance is off-catalog, retyped, or behind the
 *  catalog's layout revision — the migration effect has work pending. */
function catalogNeedsMigration(
  catalog: ManagedWidget[],
  widgets: WidgetInstance[],
  targetVersion: number,
): boolean {
  return widgets.some((widget) => {
    const config = widget.config as {
      widgetKey?: string;
      managedLayoutVersion?: number;
    } | null;
    const desired = catalog.find((entry) => entry.widgetKey === config?.widgetKey);
    return !desired || desired.type !== widget.type || (config?.managedLayoutVersion ?? 0) < targetVersion;
  });
}

export function useManagedBoardMaintenance({
  managedCatalog,
  initialLoading,
  canMaintain,
  widgets,
  direct,
}: Omittable<{
  managedCatalog?: ManagedWidget[];
  initialLoading: boolean;
  canMaintain: boolean;
  widgets: WidgetInstance[];
  direct: WidgetViewStore["direct"];
}>) {
  // A managed catalog is a set, keyed by `widgetKey`. Older clients could seed
  // from a stale cached empty response and persist duplicates before the
  // network layout arrived. Keep the first widget for each key and retire any
  // extras so an affected board repairs itself the next time a manager opens it.
  useEffect(() => {
    if (!managedCatalog || initialLoading || !canMaintain) return;
    const catalogKeys = new Set(managedCatalog.map((widget) => widget.widgetKey));
    const seen = new Set<string>();
    const duplicateIds: string[] = [];
    for (const widget of widgets) {
      const key = (widget.config as { widgetKey?: unknown } | null)?.widgetKey;
      if (typeof key !== "string" || !catalogKeys.has(key)) continue;
      if (seen.has(key)) duplicateIds.push(widget.id);
      else seen.add(key);
    }
    // Remove one at a time. The optimistic delete re-renders immediately, then
    // this effect handles the next duplicate without overlapping rollback state.
    const duplicateId = duplicateIds[0];
    if (duplicateId) direct.remove(duplicateId, { compact: false });
  }, [managedCatalog, initialLoading, canMaintain, widgets, direct]);

  // Managed catalogs can intentionally revise their designed home layout. The
  // revision lives in each seed config so the migration is one-time and
  // survives reloads without changing the persisted view key. Existing on/off
  // choices remain intact except newly introduced default widgets; removed
  // catalog entries are retired, and a widget whose type changed is replaced
  // in the same slot.
  const didMigrateManagedLayoutRef = useRef(false);
  useEffect(() => {
    if (!managedCatalog || initialLoading || !canMaintain || widgets.length === 0) return;
    if (didMigrateManagedLayoutRef.current) return;

    const targetVersion = migrationTargetVersion(managedCatalog);
    if (targetVersion === 0) return;

    // Duplicate repair runs in the preceding effect. Do not migrate the same
    // stale snapshot while one of those duplicate rows is being removed.
    if (hasDuplicateKeys(widgets)) return;

    if (!catalogNeedsMigration(managedCatalog, widgets, targetVersion)) return;
    didMigrateManagedLayoutRef.current = true;

    const catalogByKey = new Map(managedCatalog.map((widget) => [widget.widgetKey, widget]));
    const migratedKeys = new Set<string>();
    const moves: WidgetMove[] = [];

    for (const widget of widgets) {
      const widgetKey = (widget.config as { widgetKey?: string } | null)?.widgetKey;
      const desired = widgetKey ? catalogByKey.get(widgetKey) : undefined;
      if (!desired || desired.type !== widget.type) {
        direct.remove(widget.id, { compact: false });
        continue;
      }

      migratedKeys.add(desired.widgetKey);
      const seed = managedSeed(desired);
      direct.updateConfig(widget.id, seed.config);
      if (widget.x !== seed.x || widget.y !== seed.y || widget.w !== seed.w || widget.h !== seed.h) {
        moves.push({ id: widget.id, x: seed.x, y: seed.y, w: seed.w, h: seed.h });
      }
    }

    if (moves.length > 0) direct.move(moves);
    const missingDefaults = managedCatalog
      .filter((widget) => widget.defaultOn && !migratedKeys.has(widget.widgetKey))
      .map(managedSeed);
    if (missingDefaults.length > 0) direct.seed(missingDefaults);
  }, [managedCatalog, initialLoading, canMaintain, widgets, direct]);

  // Gravity self-heal: the visible catalog set must always read as a settled
  // board. Toggle-off compacts at the source now, but boards shaped by the
  // old fixed-slot toggles — and migrations that re-home designed slots
  // around hidden widgets — still carry vertical holes. Float everything up
  // and persist the correction; a compacted layout is a fixed point, so the
  // effect is idempotent. It stands down while ANY other correction is in
  // flight: duplicate rows overlap (compaction would shuffle them), a pending
  // migration is about to issue its own designed-slot moves (compacting the
  // same stale snapshot would race that batch), and an optimistic `temp-` add
  // gets its x/y restored from the server on swap-in (a local compaction
  // would be silently undone).
  useEffect(() => {
    if (!managedCatalog || initialLoading || !canMaintain || widgets.length === 0) return;
    if (widgets.some((widget) => widget.id.startsWith("temp-"))) return;
    if (hasDuplicateKeys(widgets)) return;
    const targetVersion = migrationTargetVersion(managedCatalog);
    if (targetVersion > 0 && catalogNeedsMigration(managedCatalog, widgets, targetVersion)) {
      return;
    }
    const layout: GridLayoutItem[] = widgets.map((widget) => ({
      id: widget.id,
      x: widget.x,
      y: widget.y,
      w: widget.w,
      h: widget.h,
    }));
    const moves = diffMoves(layout, compact(layout));
    if (moves.length > 0) direct.move(moves);
  }, [managedCatalog, initialLoading, canMaintain, widgets, direct]);
}
